import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const EditMediaModal = ({ media, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    fileName: media?.file_name,
    title: media?.title || '',
    description: media?.description || '',
    altText: media?.alt_text || '',
    downloadEnabled: media?.download_enabled
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...media,
      file_name: formData?.fileName,
      title: formData?.title,
      description: formData?.description,
      alt_text: formData?.altText,
      download_enabled: formData?.downloadEnabled
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-warm-2xl">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon name="Edit2" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-semibold">Edit Media</h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                Update media information and settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="aspect-video bg-muted rounded-lg overflow-hidden">
            <img
              src={media?.thumbnail_url || media?.file_url}
              alt={media?.alt_text || `Media: ${media?.file_name}`}
              className="w-full h-full object-cover"
            />
          </div>

          <Input
            label="File Name"
            type="text"
            value={formData?.fileName}
            onChange={(e) => handleChange('fileName', e?.target?.value)}
            required
          />

          <Input
            label="Title"
            type="text"
            value={formData?.title}
            onChange={(e) => handleChange('title', e?.target?.value)}
            placeholder="Optional display title"
          />

          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              placeholder="Add a description for this media file..."
            />
          </div>

          <Input
            label="Alt Text (for accessibility)"
            type="text"
            value={formData?.altText}
            onChange={(e) => handleChange('altText', e?.target?.value)}
            placeholder="Describe what's in this image/video"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground mb-1">File Type</p>
              <p className="text-sm font-medium capitalize">{media?.type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">File Size</p>
              <p className="text-sm font-medium data-text">
                {(media?.fileSize / 1048576)?.toFixed(2)} MB
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Upload Date</p>
              <p className="text-sm font-medium">
                {new Date(media.uploadDate)?.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Downloads</p>
              <p className="text-sm font-medium data-text">{media?.downloads}</p>
            </div>
          </div>

          <Checkbox
            label="Enable Downloads"
            description="Allow visitors to download this media file"
            checked={formData?.downloadEnabled}
            onChange={(e) => handleChange('downloadEnabled', e?.target?.checked)}
          />

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              className="flex-1"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMediaModal;